// ═══════════════════════════════════════════════════════════════
// Map Engine — Renderer
// All Canvas 2D drawing lives here. Stateless: receives layout +
// Visual state each frame and draws everything from scratch.
// ═══════════════════════════════════════════════════════════════

import type { Camera } from "./camera";
import {
  GRID_SIZE,
  HANDLE_SIZE,
  STATUS,
  TABLE_BORDER_RADIUS,
  THEME,
  ZONE_HEADER_H,
} from "./constants";
import { drawDotGrid, roundedRect } from "./primitives";
import type { MapConnection, MapFixedZone, MapLayout, MapTable, MapZone } from "./types";

/** Visual state flags passed in each frame (owned by the engine) */
export interface RenderState {
  selectedTableId: string | null;
  hoveredTableId: string | null;
  selectedZoneId: string | null;
  highlightedTableIds: string[];
  glowingTableIds: string[];
  connectMode: boolean;
  connectFirstId: string | null;
  startTime: number;
  isEditMode: boolean;
  /** Optional override for the canvas background. When provided, the
   * renderer paints this color in place of ``THEME.bg`` — used to
   * signal time-travel ("simulation") mode without subclassing the
   * renderer or threading another argument through the API. */
  bgOverride?: string | null;
}

const LERP_SPEED = 0.18;

/** Parse a #rrggbb (or #rgb) hex string and rebuild it as rgba(...). */
function hexToRgba(hex: string, alpha: number): string {
  const six = /^#?([a-f0-9]{6})$/i.exec(hex);
  if (six) {
    const n = parseInt(six[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }
  const three = /^#?([a-f0-9]{3})$/i.exec(hex);
  if (three) {
    const r = parseInt(three[1][0] + three[1][0], 16);
    const g = parseInt(three[1][1] + three[1][1], 16);
    const b = parseInt(three[1][2] + three[1][2], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgba(99, 102, 241, ${alpha})`;
}

export class Renderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  width = 0;
  height = 0;

  /** Animated scale per table id — lerps toward target each frame */
  private tableScales = new Map<string, number>();
  /** Glow opacity lerps between 0 and 1 so the connection halo fades in/out */
  private glowOpacity = 0;
  /** Ids to keep rendering while fading out (state.glowingTableIds is already empty) */
  private fadingGlowIds: string[] = [];
  /** True when animations are still in flight and need another frame */
  needsAnimationFrame = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
    this.resize();
  }

  /** Re-measure the canvas after a window/container resize */
  resize() {
    this.dpr = window.devicePixelRatio || 1;
    this.width = this.canvas.clientWidth;
    this.height = this.canvas.clientHeight;
    this.canvas.width = this.width * this.dpr;
    this.canvas.height = this.height * this.dpr;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  // ── Main entry ──────────────────────────────────────────────

  render(camera: Camera, layout: MapLayout, state: RenderState) {
    this.needsAnimationFrame = false;
    const { ctx } = this;
    const W = this.width;
    const H = this.height;

    // Background. ``clearRect`` gives us a transparent canvas; we then
    // overlay the fill only when there is a ``bgOverride`` (time-travel
    // tints the surface to signal simulation mode). With no override
    // the wrapper element behind the canvas — and the brand mark
    // floating between them — show through, which is what the
    // operator should see in normal use.
    ctx.clearRect(0, 0, W, H);
    if (state.bgOverride) {
      ctx.fillStyle = state.bgOverride;
      ctx.fillRect(0, 0, W, H);
    }

    // Dot grid (screen space)
    drawDotGrid(ctx, W, H, camera.offsetX, camera.offsetY, camera.scale, GRID_SIZE, THEME.gridDot);

    // World transform
    ctx.save();
    ctx.translate(camera.offsetX, camera.offsetY);
    ctx.scale(camera.scale, camera.scale);

    this.drawZones(ctx, layout.zones, layout.tables, state);
    this.drawFixedZones(ctx, layout.fixedZones);
    this.drawConnections(ctx, layout.connections, layout.tables, layout.zones);
    this.drawTableGlows(ctx, layout.tables, layout.zones, state.glowingTableIds);
    this.drawTables(ctx, layout.tables, state);

    ctx.restore();
  }

  /**
   * Soft colored halo behind each table whose id is in ``ids``. Drawn before
   * ``drawTables`` so it visually sits *underneath* the table fill — the only
   * place this can happen is on the canvas itself, since the canvas is
   * opaque and any HTML overlay would land on top.
   *
   * Lerps ``glowOpacity`` toward 1 when ids is non-empty and toward 0 when it
   * empties out, so the halo fades in/out instead of popping. We hold onto
   * the last ids while the fade-out is still running, since by then the
   * incoming ``ids`` list is already empty.
   */
  private drawTableGlows(
    ctx: CanvasRenderingContext2D,
    tables: MapTable[],
    zones: MapZone[],
    ids: string[],
  ) {
    const target = ids.length > 0 ? 1 : 0;
    if (ids.length > 0) {
      this.fadingGlowIds = ids;
    }

    this.glowOpacity += (target - this.glowOpacity) * LERP_SPEED;
    if (Math.abs(this.glowOpacity - target) < 0.01) {
      this.glowOpacity = target;
      if (target === 0) this.fadingGlowIds = [];
    } else {
      this.needsAnimationFrame = true;
    }

    if (this.glowOpacity <= 0.01 || this.fadingGlowIds.length === 0) return;

    const lookup = new Set(this.fadingGlowIds);
    const zoneById = new Map(zones.map((z) => [z.id, z]));
    ctx.save();
    ctx.globalAlpha = this.glowOpacity;
    ctx.filter = "blur(10px)";
    for (const t of tables) {
      if (!lookup.has(t.id)) continue;
      // Pick the zone's border color so the halo visually matches whichever
      // group the user has organised the tables into. Fallback to a neutral
      // indigo when the table isn't assigned to a zone.
      const zone = t.zone ? zoneById.get(t.zone) : null;
      ctx.fillStyle = hexToRgba(zone?.border ?? "#6366f1", 0.55);
      // Slightly larger than the table so the visible glow extends past it
      // even before the blur softens the edges.
      const pad = 6;
      const x = t.x - pad;
      const y = t.y - pad;
      const w = t.w + pad * 2;
      const h = t.h + pad * 2;
      if (t.shape === "circle") {
        ctx.beginPath();
        ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
        ctx.fill();
      } else {
        roundedRect(ctx, x, y, w, h, TABLE_BORDER_RADIUS + pad, true, false);
      }
    }
    ctx.restore();
  }

  // ── Editable zones ──────────────────────────────────────────

  private drawZones(
    ctx: CanvasRenderingContext2D,
    zones: MapZone[],
    tables: MapTable[],
    state: RenderState,
  ) {
    const isEdit = state.isEditMode;

    for (const z of zones) {
      const isSel = state.selectedZoneId === z.id;
      const isUnavailable = z.isAvailable === false;

      if (z.isBlock) {
        // ── Block: solid fill, centered label. The Claude Design
        // handoff styles structural blocks (bar counter, kitchen,
        // restroom) as a neutral muted strip — colours arrive
        // pre-mixed via the adapter, so we paint them at full alpha
        // and skip the legacy 0.7 attenuation. ──
        const blockFillAlpha = z.preMixedColors ? 1 : isEdit ? 0.5 : 0.7;
        const blockStrokeAlpha = z.preMixedColors ? 1 : isEdit ? 0.7 : 1;
        ctx.fillStyle = z.color;
        ctx.globalAlpha = blockFillAlpha;
        roundedRect(ctx, z.x, z.y, z.w, z.h, 10, true, false);
        ctx.globalAlpha = blockStrokeAlpha;
        ctx.strokeStyle = z.border;
        ctx.lineWidth = isSel ? 2.5 : 1;
        roundedRect(ctx, z.x, z.y, z.w, z.h, 10, false, true);
        ctx.globalAlpha = 1;

        // Label uses the block's own muted colour (e.g. ``#8a8175``
        // for the bar counter) and the brand sans at 15 px / weight
        // 500 / letter-spacing 0.22 em per the design tokens.
        ctx.fillStyle = z.preMixedColors ? z.labelColor : THEME.fixedZoneText;
        ctx.font = `500 15px ${THEME.font}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.letterSpacing = "0.22em";
        ctx.fillText(z.label, z.x + z.w / 2, z.y + z.h / 2);
        ctx.letterSpacing = "0";
      } else {
        // ── Zone: dashed border (edit), header bar, table count ──
        const count = tables.filter((t) => t.zone === z.id).length;

        // Unavailable zones swap to the same neutral grey palette as
        // unavailable tables so the off-limits state reads at a glance —
        // no extra label suffix needed, the colour itself signals it.
        const fill = isUnavailable ? STATUS.unavailable.bg : z.color;
        const stroke = isUnavailable ? STATUS.unavailable.border : z.border;
        const labelColor = isUnavailable ? STATUS.unavailable.color : z.labelColor;

        // ``preMixedColors`` zones (Seim·Seim brand palette) arrive
        // with their fills / borders / labels already mixed against
        // the cream background — no extra ``globalAlpha`` needed,
        // we paint them as solids. Other tenants keep the legacy
        // attenuation so their saturated backend colours don't blow
        // out the canvas.
        const preMixed = z.preMixedColors === true;

        ctx.fillStyle = fill;
        ctx.globalAlpha = preMixed ? 1 : isEdit ? 0.3 : 0.5;
        roundedRect(ctx, z.x, z.y, z.w, z.h, 14, true, false);
        ctx.globalAlpha = 1;

        if (isEdit) {
          ctx.setLineDash([8, 5]);
        }
        ctx.strokeStyle = stroke;
        ctx.lineWidth = isSel ? 2.5 : 1.5;
        roundedRect(ctx, z.x, z.y, z.w, z.h, 14, false, true);
        ctx.setLineDash([]);

        // Pre-mixed (brand) zones drop the header bar entirely — the
        // Claude Design handoff places the title directly on top of
        // the zone's soft fill, no extra strip. Legacy zones keep
        // the prior recipe so the title stays anchored against
        // saturated backend colours.
        if (!preMixed) {
          ctx.fillStyle = stroke;
          ctx.globalAlpha = isEdit ? 0.25 : 0.4;
          roundedRect(ctx, z.x + 1, z.y + 1, z.w - 2, ZONE_HEADER_H, 13, true, false);
          ctx.globalAlpha = 1;
        }

        // Label (left) — Ostia Antica per the Claude Design tokens
        // guide. Bumped to 17 px / 0.15em letter-spacing so the title
        // reads at a glance once the camera zoom drops below 1.0,
        // which the host always sees on the fit-to-screen view.
        // ``letterSpacing`` is the modern canvas API; older browsers
        // ignore it and still render the label (just tighter).
        ctx.fillStyle = labelColor;
        ctx.font = `500 17px 'Ostia Antica', ${THEME.font}`;
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.letterSpacing = "0.15em";
        const headerY = preMixed ? z.y + 26 : z.y + ZONE_HEADER_H / 2;
        ctx.fillText(z.label, z.x + 12, headerY);
        ctx.letterSpacing = "0";

        // Right side: table count, or a "fuera de servicio" badge when
        // the zone is disabled — operators need to know the layout
        // they're about to edit is currently turned off for bookings.
        ctx.font = `600 10px ${THEME.font}`;
        ctx.textAlign = "right";
        if (isUnavailable) {
          const badge = "FUERA DE SERVICIO";
          ctx.letterSpacing = "0.08em";
          const padX = 6;
          const padY = 3;
          const textW = ctx.measureText(badge).width;
          const badgeH = 16;
          const badgeW = textW + padX * 2;
          const badgeX = z.x + z.w - 12 - badgeW;
          const badgeY = headerY - badgeH / 2;
          ctx.fillStyle = STATUS.unavailable.border;
          ctx.globalAlpha = 0.6;
          roundedRect(ctx, badgeX, badgeY, badgeW, badgeH, 4, true, false);
          ctx.globalAlpha = 1;
          ctx.fillStyle = STATUS.unavailable.color;
          ctx.fillText(badge, z.x + z.w - 12 - padX, headerY + padY - 1);
          ctx.letterSpacing = "0";
        } else if (z.walkInOnly) {
          // No reservation belongs here — say so where staff look when
          // they pick a table by hand.
          const badge = "SOLO WALK-INS";
          ctx.letterSpacing = "0.08em";
          const padX = 6;
          const textW = ctx.measureText(badge).width;
          const badgeH = 16;
          const badgeW = textW + padX * 2;
          const badgeX = z.x + z.w - 12 - badgeW;
          const badgeY = headerY - badgeH / 2;
          ctx.strokeStyle = labelColor;
          ctx.lineWidth = 1;
          roundedRect(ctx, badgeX, badgeY, badgeW, badgeH, 4, false, true);
          ctx.fillStyle = labelColor;
          ctx.fillText(badge, z.x + z.w - 12 - padX, headerY);
          ctx.letterSpacing = "0";
        } else {
          ctx.globalAlpha = 0.6;
          ctx.fillText(`${count} mesas`, z.x + z.w - 12, headerY);
          ctx.globalAlpha = 1;
        }
        ctx.textAlign = "left";

        // Grip dots (edit mode only — visual drag handle)
        if (isEdit) {
          const gy = z.y + ZONE_HEADER_H / 2;
          ctx.fillStyle = labelColor;
          ctx.globalAlpha = 0.3;
          for (let row = -1; row <= 1; row++) {
            for (let col = 0; col < 2; col++) {
              ctx.beginPath();
              ctx.arc(z.x + z.w / 2 - 4 + col * 8, gy + row * 5, 1.5, 0, Math.PI * 2);
              ctx.fill();
            }
          }
          ctx.globalAlpha = 1;
        }
      }

      // Resize handles (both zones and blocks when selected)
      if (isSel) {
        const hs = HANDLE_SIZE;
        const half = hs / 2;
        const pts = [
          { x: z.x, y: z.y },
          { x: z.x + z.w / 2, y: z.y },
          { x: z.x + z.w, y: z.y },
          { x: z.x + z.w, y: z.y + z.h / 2 },
          { x: z.x + z.w, y: z.y + z.h },
          { x: z.x + z.w / 2, y: z.y + z.h },
          { x: z.x, y: z.y + z.h },
          { x: z.x, y: z.y + z.h / 2 },
        ];
        for (const p of pts) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(p.x - half, p.y - half, hs, hs);
          ctx.strokeStyle = z.border;
          ctx.lineWidth = 1.5;
          ctx.strokeRect(p.x - half, p.y - half, hs, hs);
        }
      }
    }
  }

  // ── Fixed zones (kitchen, WC, entrance…) ────────────────────

  private drawFixedZones(ctx: CanvasRenderingContext2D, fixedZones: MapFixedZone[]) {
    for (const z of fixedZones) {
      // Fill
      ctx.fillStyle = z.color;
      ctx.globalAlpha = 0.5;
      roundedRect(ctx, z.x, z.y, z.w, z.h, 10, true, false);

      // Border
      ctx.globalAlpha = 0.7;
      ctx.strokeStyle = z.border;
      ctx.lineWidth = 1;
      roundedRect(ctx, z.x, z.y, z.w, z.h, 10, false, true);
      ctx.globalAlpha = 1;

      // Label
      ctx.fillStyle = THEME.fixedZoneText;
      ctx.font = `600 14px ${THEME.font}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(z.label, z.x + z.w / 2, z.y + z.h / 2 - (z.isBar ? 8 : 0));

      // Bar capacity
      if (z.isBar && z.capacity) {
        ctx.font = `500 11px ${THEME.font}`;
        ctx.fillStyle = THEME.fixedZoneSecondary;
        ctx.fillText(`${z.capacity}p`, z.x + z.w / 2, z.y + z.h / 2 + 10);
      }
    }
  }

  // ── Table-to-table connection lines ─────────────────────────

  private drawConnections(
    ctx: CanvasRenderingContext2D,
    connections: MapConnection[],
    tables: MapTable[],
    zones: MapZone[],
  ) {
    const zoneById = new Map(zones.map((z) => [z.id, z]));
    for (const [aId, bId, activeColor] of connections) {
      const a = tables.find((t) => t.id === aId);
      const b = tables.find((t) => t.id === bId);
      if (!a || !b) {
        continue;
      }

      if (activeColor) {
        ctx.setLineDash([]);
        ctx.strokeStyle = activeColor;
        ctx.lineWidth = 3;
        ctx.globalAlpha = 0.8;
      } else {
        // Solid line in the zone's border color so the visual link reads as
        // "these tables can be combined within this zone". Falls back to
        // ``THEME.connectionLine`` for tables without a zone assignment.
        const zone = a.zone ? zoneById.get(a.zone) : null;
        ctx.setLineDash([]);
        ctx.strokeStyle = zone?.border ?? THEME.connectionLine;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.7;
      }

      ctx.beginPath();
      ctx.moveTo(a.x + a.w / 2, a.y + a.h / 2);
      ctx.lineTo(b.x + b.w / 2, b.y + b.h / 2);
      ctx.stroke();
    }

    ctx.globalAlpha = 1;
  }

  // ── Tables ──────────────────────────────────────────────────

  private drawTables(ctx: CanvasRenderingContext2D, tables: MapTable[], state: RenderState) {
    for (const t of tables) {
      const cfg = STATUS[t.status];
      if (!cfg) {
        continue;
      }

      const cx = t.x + t.w / 2;
      const cy = t.y + t.h / 2;
      const isSel = state.selectedTableId === t.id;
      const isHighlighted = state.highlightedTableIds.includes(t.id);
      const isActive = isSel || isHighlighted;
      const isHov = state.hoveredTableId === t.id;
      // Both "blocked" and "unavailable" tables render dimmed so operators
      // see at a glance that they can't be selected for new bookings.
      const isBlocked = t.status === "blocked" || t.status === "unavailable";

      // Animated scale on hover/select
      const targetScale = isSel ? 1.12 : isHighlighted ? 1.08 : isHov ? 1.05 : 1;
      const prevScale = this.tableScales.get(t.id) ?? 1;
      const scale = prevScale + (targetScale - prevScale) * LERP_SPEED;
      this.tableScales.set(t.id, scale);
      if (Math.abs(scale - targetScale) > 0.002) {
        this.needsAnimationFrame = true;
      }

      if (Math.abs(scale - 1) > 0.001) {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.scale(scale, scale);
        ctx.translate(-cx, -cy);
      }

      this.drawTableBody(ctx, t, cfg, cx, cy, isActive, isHov, isBlocked);
      this.drawTableStroke(ctx, t, cfg, cx, cy, isActive, isHov, isBlocked);

      if (state.connectMode && state.connectFirstId === t.id) {
        this.drawConnectHighlight(ctx, t, cx, cy);
      }

      this.drawTableLabel(ctx, t, cfg, cx, cy, state.startTime);

      if (Math.abs(scale - 1) > 0.001) {
        ctx.restore();
      }
    }
  }

  // ── Table sub-draws ─────────────────────────────────────────

  private drawTableBody(
    ctx: CanvasRenderingContext2D,
    t: MapTable,
    cfg: { bg: string },
    cx: number,
    cy: number,
    isSel: boolean,
    isHov: boolean,
    isBlocked: boolean,
  ) {
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.07)";
    ctx.shadowBlur = isSel || isHov ? 14 : 6;
    ctx.shadowOffsetY = isSel || isHov ? 3 : 1;
    ctx.fillStyle = cfg.bg;
    ctx.globalAlpha = isBlocked ? 0.45 : 1;

    if (t.shape === "circle") {
      ctx.beginPath();
      ctx.arc(cx, cy, t.w / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      roundedRect(ctx, t.x, t.y, t.w, t.h, TABLE_BORDER_RADIUS, true, false);
    }
    ctx.restore();
  }

  private drawTableStroke(
    ctx: CanvasRenderingContext2D,
    t: MapTable,
    cfg: { color: string },
    cx: number,
    cy: number,
    isSel: boolean,
    isHov: boolean,
    isBlocked: boolean,
  ) {
    ctx.strokeStyle = cfg.color;
    ctx.lineWidth = isSel ? 2.8 : isHov ? 2.2 : 1.5;
    ctx.globalAlpha = isBlocked ? 0.45 : 1;

    if (t.shape === "circle") {
      ctx.beginPath();
      ctx.arc(cx, cy, t.w / 2, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      roundedRect(ctx, t.x, t.y, t.w, t.h, TABLE_BORDER_RADIUS, false, true);
    }
    ctx.globalAlpha = 1;
  }

  private drawConnectHighlight(ctx: CanvasRenderingContext2D, t: MapTable, cx: number, cy: number) {
    ctx.setLineDash([3, 3]);
    ctx.strokeStyle = THEME.connectHighlight;
    ctx.lineWidth = 2.5;

    if (t.shape === "circle") {
      ctx.beginPath();
      ctx.arc(cx, cy, t.w / 2 + 5, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      roundedRect(ctx, t.x - 5, t.y - 5, t.w + 10, t.h + 10, 13, false, true);
    }
    ctx.setLineDash([]);
  }

  private drawTableLabel(
    ctx: CanvasRenderingContext2D,
    t: MapTable,
    cfg: { color: string },
    cx: number,
    cy: number,
    _startTime: number,
  ) {
    const hasTimer = t.timer > 0 && t.status === "occupied";
    const hasNoShowTimer = t.noShowTimer >= 0 && t.status === "reserved";
    const hasSubline = hasTimer || hasNoShowTimer;

    // Name
    ctx.fillStyle = cfg.color;
    const fs = t.w > 78 ? 18 : t.w > 65 ? 15 : 13;
    ctx.font = `700 ${fs}px ${THEME.font}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(t.label, cx, cy - (hasSubline ? 12 : 8));

    // Capacity pill — a soft tinted rounded badge with a person glyph + the
    // count, sitting just below the name. Reads cleaner than the floating
    // icon + number, and the tinted bg ties it to the status color.
    ctx.font = `600 12px ${THEME.font}`;
    const capStr = String(t.capacity);
    const textW = ctx.measureText(capStr).width;
    const iconW = 9;
    const gapInternal = 4;
    const padX = 7;
    const pillH = 18;
    const contentW = iconW + gapInternal + textW;
    const pillW = contentW + padX * 2;
    const pillX = cx - pillW / 2;
    const pillY = cy + (hasSubline ? -2 : 5);

    ctx.fillStyle = cfg.color;
    ctx.globalAlpha = 0.16;
    roundedRect(ctx, pillX, pillY, pillW, pillH, pillH / 2, true, false);

    ctx.globalAlpha = 1;
    ctx.fillStyle = cfg.color;
    const contentX = cx - contentW / 2;
    const iconCY = pillY + pillH / 2;
    const iconX = contentX + iconW / 2;
    // Person glyph (head + shoulders)
    ctx.beginPath();
    ctx.arc(iconX, iconCY - 3, 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(iconX, iconCY + 2.5, 4, Math.PI, 2 * Math.PI);
    ctx.closePath();
    ctx.fill();
    // Count
    ctx.textAlign = "left";
    ctx.fillText(capStr, contentX + iconW + gapInternal, iconCY);
    ctx.textAlign = "center";

    // Timer (t.timer = minutes to display directly)
    if (hasTimer) {
      const total = Math.max(0, t.timer);
      const hrs = Math.floor(total / 60);
      const mins = total % 60;
      ctx.font = `600 11px ${THEME.font}`;
      ctx.fillStyle = cfg.color;
      ctx.globalAlpha = 0.75;
      ctx.fillText(`${hrs}h${String(mins).padStart(2, "0")}m`, cx, cy + 24);
      ctx.globalAlpha = 1;
    }

    // No-show grace countdown (red)
    if (hasNoShowTimer) {
      ctx.font = `700 11px ${THEME.font}`;
      ctx.fillStyle = "#dc2626";
      const nsLabel = t.noShowTimer > 0 ? `No show ${t.noShowTimer}m` : "No show";
      ctx.fillText(nsLabel, cx, cy + 24);
    }

    // VIP star — top-right corner (solid orange star drawn with canvas path)
    if (t.icon === "vip") {
      const starX = t.x + t.w / 2;
      const starY = t.y + 10;
      const r1 = 6;
      const r2 = 3;
      ctx.save();
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const outerAngle = (i * 2 * Math.PI) / 5 - Math.PI / 2;
        const innerAngle = outerAngle + Math.PI / 5;
        ctx.lineTo(starX + r1 * Math.cos(outerAngle), starY + r1 * Math.sin(outerAngle));
        ctx.lineTo(starX + r2 * Math.cos(innerAngle), starY + r2 * Math.sin(innerAngle));
      }
      ctx.closePath();
      ctx.fillStyle = "#f97316";
      ctx.globalAlpha = 1;
      ctx.fill();
      ctx.restore();
    }
  }
}
